import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { getPlayerEmoji } from '../utils/avatars';
import { apiClient } from '../api/client';
import { buildGameWebSocketUrl } from '../utils/ws';
import { cn } from '../utils/cn';
import { useLanguage } from '../contexts/LanguageContext';

export const VoteScreen = ({ gameData, onComplete }) => {
    const { t } = useLanguage();
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [timeLeft, setTimeLeft] = useState(gameData.timeLeft || 30);
    const [hasVoted, setHasVoted] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const timerIntervalRef = useRef(null);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const resultsPollRef = useRef(null);
    const serverOffsetMsRef = useRef(0);
    const phaseEndMsRef = useRef(null);
    const hasVotedRef = useRef(false);
    const gameOverHandledRef = useRef(false);
    const isMountedRef = useRef(true);

    const tg = window.Telegram?.WebApp;
    const currentUserId = tg?.initDataUnsafe?.user?.id?.toString();

    const syncServerOffset = useCallback((serverNowIso) => {
        if (!serverNowIso) return;
        const serverMs = Date.parse(serverNowIso);
        if (!Number.isNaN(serverMs)) {
            serverOffsetMsRef.current = serverMs - Date.now();
        }
    }, []);

    const computeRemainingSeconds = useCallback((endMs) => {
        const now = Date.now() + (serverOffsetMsRef.current || 0);
        return Math.max(0, Math.round((endMs - now) / 1000));
    }, []);

    const handleGameOver = useCallback((data) => {
        if (gameOverHandledRef.current) return;
        gameOverHandledRef.current = true;
        setIsWaiting(false);
        if (resultsPollRef.current) {
            clearInterval(resultsPollRef.current);
            resultsPollRef.current = null;
        }
        onComplete(data);
    }, [onComplete]);

    const fetchResultsOnce = useCallback(async () => {
        try {
            const data = await apiClient.getResults(gameData.gameId);
            if (data && Array.isArray(data.results) && data.results.length > 0) {
                handleGameOver({ type: 'game_over', ...data });
                return true;
            }
        } catch (error) {
            console.warn('VoteScreen: results fetch failed', error);
        }
        return false;
    }, [gameData.gameId, handleGameOver]);

    const startResultsPolling = useCallback(() => {
        if (resultsPollRef.current || gameOverHandledRef.current) return;
        resultsPollRef.current = setInterval(async () => {
            if (gameOverHandledRef.current) {
                clearInterval(resultsPollRef.current);
                resultsPollRef.current = null;
                return;
            }
            await fetchResultsOnce();
        }, 3000);
    }, [fetchResultsOnce]);

    const applyPhaseSnapshot = useCallback((data) => {
        if (!data) return;
        if (data.server_now) {
            syncServerOffset(data.server_now);
        }
        let endMs = null;
        if (data.phase_end_at) {
            const parsed = Date.parse(data.phase_end_at);
            if (!Number.isNaN(parsed)) endMs = parsed;
        } else if (typeof data.duration === 'number') {
            const now = Date.now() + (serverOffsetMsRef.current || 0);
            endMs = now + (data.duration * 1000);
        }

        if (endMs) {
            phaseEndMsRef.current = endMs;
            setTimeLeft(computeRemainingSeconds(endMs));
        }

        if (data.phase === 'results') {
            setIsWaiting(true);
            fetchResultsOnce().then((handled) => {
                if (!handled) {
                    startResultsPolling();
                }
            });
        }
    }, [computeRemainingSeconds, fetchResultsOnce, startResultsPolling, syncServerOffset]);

    const connectWebSocket = useCallback(() => {
        const tg = window.Telegram?.WebApp;
        const currentId = tg?.initDataUnsafe?.user?.id?.toString() || 'unknown';
        const url = buildGameWebSocketUrl(gameData.gameId, currentId);

        if (wsRef.current) {
            try {
                wsRef.current.close();
            } catch (e) {
                console.warn('VoteScreen: could not close stale WS', e);
            }
        }

        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            if (!isMountedRef.current) return;
            console.log('VoteScreen: WS Connected');
        };

        ws.onmessage = (event) => {
            if (!isMountedRef.current) return;
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'connected') {
                    syncServerOffset(data.server_now);
                    if (data.phase || data.phase_end_at || data.duration !== undefined) {
                        applyPhaseSnapshot(data);
                    }
                } else if (data.type === 'phase_change') {
                    applyPhaseSnapshot(data);
                } else if (data.type === 'game_over') {
                    handleGameOver(data);
                }
            } catch (e) {
                console.error('VoteScreen: WS Parse Error', e);
            }
        };

        ws.onclose = () => {
            if (!isMountedRef.current || gameOverHandledRef.current) return;
            console.log('VoteScreen: WS Closed');
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = setTimeout(() => {
                connectWebSocket();
            }, 2000);
        };

        ws.onerror = (e) => {
            console.error('VoteScreen: WS Error', e);
        };
    }, [applyPhaseSnapshot, gameData.gameId, handleGameOver, syncServerOffset]);

    useEffect(() => {
        isMountedRef.current = true;
        hasVotedRef.current = hasVoted;
    }, [hasVoted]);

    const handleVote = useCallback(async (playerId) => {
        if (hasVoted) return;
        setHasVoted(true);
        hasVotedRef.current = true;
        setIsWaiting(true);

        try {
            await apiClient.vote(gameData.gameId, playerId);
            startResultsPolling();
        } catch (error) {
            console.error('Vote error:', error);
            const message = error?.message || '';
            if (message.includes('voting_closed')) {
                setIsWaiting(true);
                fetchResultsOnce().then((handled) => {
                    if (!handled) {
                        startResultsPolling();
                    }
                });
                return;
            }
            setHasVoted(false);
            hasVotedRef.current = false;
            setIsWaiting(false);
        }
    }, [fetchResultsOnce, gameData.gameId, hasVoted, startResultsPolling]);

    useEffect(() => {
        isMountedRef.current = true;
        connectWebSocket();

        timerIntervalRef.current = setInterval(() => {
            const endMs = phaseEndMsRef.current;
            if (endMs) {
                const remaining = computeRemainingSeconds(endMs);
                setTimeLeft(remaining);
                if (remaining <= 0 && !hasVotedRef.current) {
                    handleVote(null);
                }
                return;
            }
            setTimeLeft(prev => {
                const next = Math.max(0, prev - 1);
                if (next <= 0 && !hasVotedRef.current) {
                    handleVote(null);
                }
                return next;
            });
        }, 1000);

        return () => {
            isMountedRef.current = false;
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
            if (resultsPollRef.current) clearInterval(resultsPollRef.current);
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [computeRemainingSeconds, connectWebSocket, handleVote]);

    const votingPlayers = gameData.players.filter(pid => pid !== currentUserId);

    if (isWaiting) {
        return (
            <div className="flex flex-col items-center justify-center h-full px-6 space-y-8 animate-fade-in">
                <div className="relative">
                    <div className="w-24 h-24 rounded-full border-4 border-cyan-500/20 border-t-cyan-500 animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center text-2xl">⏳</div>
                </div>
                <div className="text-center space-y-2">
                    <h2 className="text-xl font-bold text-white uppercase tracking-widest text-glow">{t('vote_waiting_title')}</h2>
                    <p className="text-slate-400 text-sm">{t('vote_waiting_desc')}</p>
                </div>
                <div className="px-6 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30">
                    <span className="font-mono text-xl font-bold text-cyan-400">{timeLeft}s</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full px-4 pt-6 pb-10 space-y-6 overflow-hidden">
            <div className="text-center space-y-2 shrink-0">
                <h1 className="text-2xl font-black text-white uppercase tracking-tighter text-glow italic">
                    {t('vote_title')}
                </h1>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">
                    {t('vote_subtitle')}
                </p>
            </div>

            {/* Timer Pill */}
            <div className="flex justify-center shrink-0">
                <div className={cn(
                    "flex items-center gap-3 px-6 py-2 rounded-2xl border transition-all duration-500 backdrop-blur-xl",
                    timeLeft <= 10
                        ? "bg-red-500/20 border-red-500/50 shadow-[0_0_20px_rgba(239,68,68,0.2)] animate-pulse"
                        : "bg-white/5 border-white/10"
                )}>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{t('vote_timer_left')}</span>
                    <span className={cn(
                        "font-mono text-2xl font-black tabular-nums leading-none",
                        timeLeft <= 10 ? "text-red-400" : "text-white"
                    )}>
                        {timeLeft}s
                    </span>
                </div>
            </div>

            {/* Suspects List */}
            <div className="flex-1 overflow-y-auto space-y-3 px-1 custom-scrollbar">
                {votingPlayers.map((pid, idx) => {
                    const emoji = getPlayerEmoji(pid, gameData.gameId, gameData.players);
                    const isSelected = selectedPlayer === pid;

                    return (
                        <button
                            key={pid}
                            onClick={() => setSelectedPlayer(pid)}
                            className={cn(
                                "w-full flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 text-left",
                                isSelected
                                    ? "bg-cyan-500/20 border-cyan-500 shadow-[0_0_20px_rgba(6,182,212,0.15)] translate-x-1"
                                    : "bg-white/5 border-white/10 hover:bg-white/10"
                            )}
                        >
                            <div className="text-3xl filter drop-shadow-md">{emoji}</div>
                            <div className="flex-1">
                                <div className={cn(
                                    "text-sm font-bold uppercase tracking-wider mb-0.5",
                                    isSelected ? "text-cyan-400" : "text-white"
                                )}>
                                    {t('game_player')} {gameData.players.indexOf(pid) + 1}
                                </div>
                                <div className="text-[10px] text-slate-500 font-medium uppercase tracking-widest">
                                    {t('vote_suspect')}
                                </div>
                            </div>
                            {isSelected && (
                                <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center shadow-[0_0_10px_rgba(6,182,212,0.5)]">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-4 shrink-0 px-2">
                <Button
                    variant="outline"
                    onClick={() => handleVote(null)}
                    className="h-14 rounded-2xl border-white/10 text-slate-400 hover:text-white"
                >
                    {t('vote_skip')}
                </Button>
                <Button
                    variant="primary"
                    onClick={() => handleVote(selectedPlayer)}
                    disabled={!selectedPlayer}
                    className="h-14 rounded-2xl shadow-[0_0_20px_rgba(6,182,212,0.2)]"
                >
                    {t('vote_action')}
                </Button>
            </div>
        </div>
    );
};
